/**
 * Column Definitions
 * Contains all the column definition functions for each entity type
 * (children, employees, contacts, childcares, groups, labels)
 */

import React from "react";
import type {
  EditableCellConfig,
  Child,
  Employee,
  Childcare,
} from "@/components/table/types";
import type { Contact } from "@/types/contact";
import type { Group, Label } from "@/types";
import type { TransformedContract } from "@/types/billing";
import { Link, type NavigateFunction } from "react-router";
import { useLingui } from "@lingui/react";
import type { MessageDescriptor } from "@lingui/core";
// `t` below is the caller-injected translation function, not the Lingui
// macro, so a bare `t({ id, message })` is invisible to `lingui extract`.
// `defineMessage` IS the macro: it makes the id and its source text
// extractable while returning the same descriptor `t` already accepts.
import { defineMessage } from "@lingui/macro";
import {
  models_EntityStatus,
  models_GenderType,
  models_QualificationLevel,
} from "@/types/employee";
import { BaseAvatarCell } from "@/components/table/BaseAvatarCell";
import { ChildAvatarCell } from "@/components/table/ChildAvatarCell";
import { EmployeeAvatarCell } from "@/components/table/EmployeeAvatarCell";
import { ContactAvatarCell } from "@/components/table/ContactAvatarCell";
import { AddressDisplay } from "@/components/AddressDisplay";
import { Badge } from "@/components/ui/badge";
import { StatusBadge, type StatusTone } from "@/components/ui/status-badge";
import { ScoreCell } from "@/components/ui/score-cell";
import { GroupAvatarCell, LabelAvatarCell } from "./avatarColumnHelpers";
import { formatDate } from "./dateUtils";
import { formatDateTime } from "@/lib/format";
import { formatChildAge } from "./formatChildAge";
import { i18n } from "@/lib/i18n";
import type { ActivityResponse } from "@/types/activities";
import { getActivityTypeLabels } from "@/types/activities";
import type {
  EmployeeAbsence,
  AbsenceTypeValue,
  CertificateModeValue,
} from "@/types/employeeAbsences";
import {
  ApprovalStatus,
  CertificateMode,
  CertificateStatus,
} from "@/types/employeeAbsences";
import { AbsenceTypeBadge } from "@/components/absences/AbsenceTypeBadge";
import { CertificateStatusBadge } from "@/components/absences/CertificateStatusBadge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  absenceCountBasis,
  absenceCountLabel,
} from "@/utils/absenceCounting";
import {
  Repeat,
  Pencil,
  AlertTriangle,
  MoreHorizontal,
  CheckCircle2,
  XCircle,
  FileCheck,
  CalendarRange,
  FileText,
  ChevronRight,
} from "lucide-react";
import { formatPhoneWithCountryCode } from "@/utils/referenceData";

// Define translation function type based on Lingui patterns
type TranslationFunction = (template: string | MessageDescriptor) => string;

/** Courses and course plans are either running or archived. */
const ARCHIVE_STATUS_TONE: Record<"active" | "archived", StatusTone> = {
  active: "success",
  archived: "neutral",
};

/**
 * The record name as a link, so the obvious target opens the record. The cell
 * is read-only: a link inside an inline-edit cell would nest two controls.
 */
function RecordNameLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="truncate font-medium text-foreground underline-offset-2 hover:text-primary hover:underline"
    >
      {children}
    </Link>
  );
}

function OpenRecordLink({ to, name }: { to: string; name: string }) {
  const { i18n } = useLingui();
  const label = i18n._({ id: "table.openRecord", message: "Open {name}", values: { name } });
  return (
    <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
      <Link to={to} aria-label={label} title={label} data-no-row-click>
        <ChevronRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </Button>
  );
}

/**
 * Row-end "Open" column for an editable list, where a row click would collide
 * with inline editing. Always visible and never exported (no header).
 */
export function openRecordColumn<T extends { id?: string; firstname?: string; lastname?: string }>(
  basePath: string
): EditableCellConfig<T> {
  return {
    accessorKey: "id" as keyof T,
    id: "open",
    header: "",
    editable: false,
    sortable: false,
    enableHiding: false,
    size: 48,
    cell: ({ row }) => {
      const { id, firstname, lastname } = row.original;
      if (!id) return null;
      const name = [firstname, lastname].filter(Boolean).join(" ");
      return <OpenRecordLink to={`${basePath}/${id}/edit`} name={name} />;
    },
  };
}

// Default visible columns for contacts
export const DEFAULT_CONTACT_VISIBLE_COLUMNS = [
  "avatar",
  "firstname",
  "lastname",
  "emailMain",
  "telMobile",
];

// Children column definitions
export function getChildrenColumns(
  t: TranslationFunction,
  navigate?: NavigateFunction,
  enumOptions?: {
    status?: { value: string; label: string }[];
    gender?: { value: string; label: string }[];
  },
  _options?: { availableLabels?: Array<{ id?: string; label?: string; color?: string }> }
): EditableCellConfig<Child>[] {
  // Translated once per column build, like every header in this file.
  // `readOnlyTooltip` runs per CELL RENDER, so calling `t` inside it would pay an
  // ICU format for every read-only status cell on the page on every render.
  const statusDerivedTooltip = t(
    defineMessage({
      id: "table.children.status.derivedTooltip",
      message:
        "Derived from this child's contract versions and not editable here. Change the contract and the status follows automatically.",
    })
  );

  return [
    {
      accessorKey: "id" as keyof Child,
      id: "avatar",
      header: "",
      editable: false,
      sortable: false,
      enableHiding: false, // Avatar column not selectable in manage columns
      cell: ({ row }: { row: { original: Child } }) => {
        const child = row.original;

        return (
          <ChildAvatarCell
            child={child}
            onClick={() => {
              if (navigate) {
                navigate(`/children/${child.id}/edit`);
              } else if (
                typeof window !== "undefined" &&
                window.location.pathname.includes("/children")
              ) {
                window.location.href = `/children/${child.id}/edit`;
              }
            }}
          />
        );
      },
    },
    {
      accessorKey: "childInternalId",
      header: t("table.children.columns.internalId"),
      editable: true,
      sortable: true,
      inputType: "text",
    },
    {
      accessorKey: "firstname",
      header: t("table.children.columns.firstName"),
      // On the list (navigate given) the name opens the record; the settings
      // preview keeps the plain editable cell.
      editable: !navigate,
      sortable: true,
      inputType: "text",
      ...(navigate && {
        cell: ({ row }: { row: { original: Child } }) =>
          row.original.id ? (
            <RecordNameLink to={`/children/${row.original.id}/edit`}>
              {row.original.firstname}
            </RecordNameLink>
          ) : (
            row.original.firstname
          ),
      }),
    },
    {
      accessorKey: "lastname",
      header: t("table.children.columns.lastName"),
      editable: true,
      sortable: true,
      inputType: "text",
    },
    {
      id: "labels",
      accessorKey: "labels",
      header: t("Labels"),
      editable: false,
      sortable: false,
      selectOptions: _options?.availableLabels?.map(l => ({
        value: l.id || '',
        label: l.label || '',
      })) || [],
      cell: ({ row }: { row: { original: Child } }) => {
        const labels = row.original.labels || [];
        return (
          <div className="flex flex-wrap gap-1" data-testid="labels-cell">
            {labels.map((label: { id?: string; label?: string; color?: string | null }) => (
              <Badge
                key={label.id}
                variant="secondary"
                style={{
                  backgroundColor: label.color ? `${label.color}20` : undefined,
                  borderColor: label.color || undefined,
                  color: label.color || undefined,
                }}
              >
                {label.label}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: "gender",
      header: t("table.children.columns.gender"),
      editable: true,
      sortable: true,
      inputType: "select",
      selectOptions: enumOptions?.gender || [
        {
          label: t("children.enums.gender.male"),
          value: models_GenderType.GenderMale,
        },
        {
          label: t("children.enums.gender.female"),
          value: models_GenderType.GenderFemale,
        },
        {
          label: t("children.enums.gender.other"),
          value: models_GenderType.GenderOther,
        },
        {
          label: t("children.enums.gender.unknown"),
          value: models_GenderType.GenderUnknown,
        },
      ],
    },
    {
      accessorKey: "birthdate",
      header: t("table.children.columns.birthDate"),
      editable: true,
      sortable: true,
      inputType: "date",
      cell: ({ row }: { row: { original: Child } }) => {
        const date = row.original.birthdate;
        if (!date) return null;
        const formattedDate = formatDate(date, i18n.locale);
        const age = formatChildAge(date, i18n);
        return (
          <div className="flex flex-col">
            <span>{formattedDate}</span>
            {age && (
              <span className="text-xs text-muted-foreground">{age}</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: t("table.children.columns.status"),
      // Issue #1224: the derivation job owns `status` only for a row it can derive.
      // A child with no non-CANCELLED contract version is skipped by the job, so
      // the admin owns the value instead. An absent flag means the endpoint did
      // not compute it -- treat as derived.
      editable: (row) => row.statusDerived === false,
      // Issue #1224: a derived child's status cell is inert, so it explains
      // itself. A child the job SKIPS has a writable cell and needs no tooltip.
      readOnlyTooltip: (row) =>
        row.statusDerived === false ? undefined : statusDerivedTooltip,
      sortable: true,
      inputType: "select",
      selectOptions: enumOptions?.status || [
        {
          label: t({ id: "common.active", message: "Active" }),
          value: models_EntityStatus.StatusActive,
        },
        {
          label: t({ id: "common.inactive", message: "Inactive" }),
          value: models_EntityStatus.StatusInactive,
        },
        {
          label: t({ id: "common.upcoming", message: "Upcoming" }),
          value: models_EntityStatus.StatusUpcoming,
        },
      ],
    },
    {
      // Contract signature status (issue #1248). The column shows the raw
      // boolean and nothing else: staleness is a DETAIL-response field, so the
      // list never pays a query for it.
      accessorKey: "contractSigned",
      header: t("table.children.columns.contractSigned"),
      editable: false,
      sortable: false,
      inputType: "checkbox",
      // A read-only checkbox column renders its stored value as text, and a raw
      // "true"/"" reads as an unlabelled state. Yes/No is the accessible form.
      formatter: (value) => (value === true ? t("common.yes") : t("common.no")),
    },
    {
      accessorKey: "notes",
      header: t("table.children.columns.notes"),
      editable: true,
      sortable: false,
      inputType: "textarea",
    },
    {
      accessorKey: "specialCare",
      header: t("table.children.columns.specialCare"),
      editable: true,
      sortable: false,
      inputType: "textarea",
    },
    {
      accessorKey: "createdAt",
      header: t("table.children.columns.createdAt"),
      editable: false,
      sortable: true,
      inputType: "date",
    },
  ];
}

// Employee column definitions
export function getEmployeeColumns(
  t: TranslationFunction,
  navigate?: NavigateFunction,
  enumOptions?: {
    status?: { value: string; label: string }[];
    gender?: { value: string; label: string }[];
    qualificationLevel?: { value: string; label: string }[];
    role?: { value: string; label: string }[];
  },
  _options?: { availableLabels?: Array<{ id?: string; label?: string; color?: string }> }
): EditableCellConfig<Employee>[] {
  // See the note in getChildrenColumns: translated at column-build time, not per
  // cell render.
  const statusDerivedTooltip = t(
    defineMessage({
      id: "table.employees.status.derivedTooltip",
      message:
        "Derived from this employee's work contracts and not editable here. Add or change a work contract and the status follows automatically.",
    })
  );
  const contractDerivedTooltip = t(
    defineMessage({
      id: "table.employees.contractDerivedTooltip",
      message:
        "Taken from the work contract that is active today and not editable here. Change it on the employee's Contracts tab.",
    })
  );
  const qualificationLevelDerivedTooltip = t(
    defineMessage({
      id: "table.employees.qualificationLevel.derivedTooltip",
      message:
        "Derived from the employee's currently valid qualifications and not editable here. Without a valid qualification the level stays as last set — add a qualification on the employee form and it follows automatically.",
    })
  );

  return [
    {
      accessorKey: "id" as keyof Employee,
      id: "avatar",
      header: "",
      editable: false,
      sortable: false,
      enableHiding: true, // Allow hiding the avatar column
      cell: ({ row }: { row: { original: Employee } }) => {
        const employee = row.original;
        return (
          <EmployeeAvatarCell
            employee={employee}
            onClick={() => {
              if (navigate) {
                navigate(`/employees/${employee.id}/edit`);
              } else if (
                typeof window !== "undefined" &&
                window.location.pathname.includes("/employees")
              ) {
                window.location.href = `/employees/${employee.id}/edit`;
              }
            }}
          />
        );
      },
    },
    {
      accessorKey: "firstname",
      header: t("First Name"),
      // See getChildrenColumns: a link on the list, editable in the preview.
      editable: !navigate,
      sortable: true,
      inputType: "text",
      ...(navigate && {
        cell: ({ row }: { row: { original: Employee } }) =>
          row.original.id ? (
            <RecordNameLink to={`/employees/${row.original.id}/edit`}>
              {row.original.firstname}
            </RecordNameLink>
          ) : (
            row.original.firstname
          ),
      }),
    },
    {
      accessorKey: "lastname",
      header: t("Last Name"),
      editable: true,
      sortable: true,
      inputType: "text",
    },
    {
      id: "labels",
      accessorKey: "labels",
      header: t("Labels"),
      editable: false,
      sortable: false,
      size: 120,
      selectOptions: _options?.availableLabels?.map(l => ({
        value: l.id || '',
        label: l.label || '',
      })) || [],
      cell: ({ row }: { row: { original: Employee } }) => {
        const labels = row.original.labels || [];
        return (
          <div className="flex flex-wrap gap-1" data-testid="labels-cell">
            {labels.map((label: { id?: string; label?: string; color?: string | null }) => (
              <Badge
                key={label.id}
                variant="secondary"
                style={{
                  backgroundColor: label.color ? `${label.color}20` : undefined,
                  borderColor: label.color || undefined,
                  color: label.color || undefined,
                }}
              >
                {label.label}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: "email",
      header: t("Email"),
      editable: true,
      sortable: true,
      inputType: "email",
    },
    {
      accessorKey: "phone",
      header: t("Phone"),
      editable: true,
      sortable: true,
      inputType: "text",
      cell: ({ row }: { row: { original: Employee } }) => {
        const phone = row.original.phone;
        const countryCode = (row.original as Record<string, unknown>).phoneCountryCode as string | undefined;
        if (!phone) return null;
        return formatPhoneWithCountryCode(phone, countryCode) ?? phone;
      },
    },
    {
      accessorKey: "emailBusiness",
      header: t({ id: "employees.fields.emailBusiness", message: "Business email" }),
      editable: true,
      sortable: true,
      inputType: "email",
    },
    {
      accessorKey: "phoneBusiness",
      header: t({ id: "employees.fields.phoneBusiness", message: "Business phone" }),
      editable: true,
      sortable: true,
      inputType: "text",
      cell: ({ row }: { row: { original: Employee } }) => {
        const phone = row.original.phoneBusiness;
        if (!phone) return null;
        return formatPhoneWithCountryCode(phone, row.original.phoneBusinessCountryCode) ?? phone;
      },
    },
    {
      accessorKey: "gender",
      header: t("Gender"),
      editable: true,
      sortable: true,
      inputType: "select",
      selectOptions: enumOptions?.gender || [
        {
          label: t("children.enums.gender.male"),
          value: models_GenderType.GenderMale,
        },
        {
          label: t("children.enums.gender.female"),
          value: models_GenderType.GenderFemale,
        },
        {
          label: t("children.enums.gender.other"),
          value: models_GenderType.GenderOther,
        },
        {
          label: t("children.enums.gender.unknown"),
          value: models_GenderType.GenderUnknown,
        },
      ],
    },
    {
      accessorKey: "birthdate",
      header: t("Birth Date"),
      editable: true,
      sortable: true,
      inputType: "date",
    },
    {
      accessorKey: "status",
      header: t("Status"),
      // Issue #1224: the derivation job owns `status` only for a row it can derive.
      // An employee with no employee_work_contracts row is skipped by the job, so
      // the admin owns the value instead. An absent flag means the endpoint did not
      // compute it -- treat as derived.
      editable: (row) => row.statusDerived === false,
      // Issue #1224: same rule as the children column, same reason.
      readOnlyTooltip: (row) =>
        row.statusDerived === false ? undefined : statusDerivedTooltip,
      sortable: true,
      inputType: "select",
      selectOptions: enumOptions?.status || [
        {
          label: t({ id: "common.active", message: "Active" }),
          value: models_EntityStatus.StatusActive,
        },
        {
          label: t({ id: "common.inactive", message: "Inactive" }),
          value: models_EntityStatus.StatusInactive,
        },
        {
          label: t({ id: "common.upcoming", message: "Upcoming" }),
          value: models_EntityStatus.StatusUpcoming,
        },
      ],
    },
    {
      accessorKey: "qualificationLevel",
      header: t("Qualification Level"),
      // DERIVED from the currently-valid employee_qualification_holdings
      // (issue #352) — not hand-editable. `inputType` and `selectOptions` stay so
      // the value still renders and the column stays filterable; only inline
      // editing is blocked, which is what stops the table submitting the column.
      editable: false,
      // Issue #1224 done-when: ONE explanation covers BOTH read-only employee
      // fields. This column is read-only on every row -- the job skips an
      // employee with no currently-valid holding, and the escape hatch is the
      // holdings panel on the edit form, not this cell.
      readOnlyTooltip: () => qualificationLevelDerivedTooltip,
      sortable: true,
      inputType: "select",
      selectOptions: enumOptions?.qualificationLevel || [
        {
          label: t("Qualified"),
          value: models_QualificationLevel.QualificationQualified,
        },
        {
          label: t("Recognition Pending"),
          value: models_QualificationLevel.QualificationRecognitionPending,
        },
        {
          label: t("Not Qualified"),
          value: models_QualificationLevel.QualificationNotQualified,
        },
      ],
    },
    // Role and hierarchy level live on the work contract (migration 179). The
    // row carries the values of the contract active today; they are edited on
    // the Contracts tab, never inline.
    {
      accessorKey: "role",
      header: t("Role"),
      editable: false,
      readOnlyTooltip: () => contractDerivedTooltip,
      sortable: true,
      inputType: "select",
      selectOptions: enumOptions?.role || [],
    },
    {
      accessorKey: "hierarchyLevel",
      header: t("Hierarchy Level"),
      editable: false,
      readOnlyTooltip: () => contractDerivedTooltip,
      sortable: true,
      inputType: "number",
    },
    {
      accessorKey: "external",
      header: t("External"),
      editable: true,
      sortable: true,
      inputType: "checkbox",
    },
    {
      accessorKey: "createdAt",
      header: t("Created At"),
      editable: false,
      sortable: true,
      inputType: "date",
    },
  ];
}

// Group column definitions
export function getGroupColumns(
  t: TranslationFunction,
  options?: {
    onAvatarClick?: (group: Group) => void;
    onEdit?: (group: Group) => void;
    childcares?: Array<{ id: string; name: string }>;
    typeOptions?: Array<{ value: string; label: string }>;
    availableLabels?: Array<{
      id: string;
      label: string;
      color?: string | null;
    }>;
  }
): EditableCellConfig<Group>[] {
  return [
    {
      id: "searchableContent",
      accessorKey: "searchableContent" as keyof Group,
      header: "",
      editable: false,
      sortable: false,
      enableHiding: true,
      cell: () => null, // Hidden column for search
    },
    {
      id: "avatar",
      accessorKey: "id" as keyof Group,
      header: "",
      editable: false,
      sortable: false,
      width: 80,
      enableHiding: true,
      cell: ({ row }: { row: { original: Group } }) => {
        const group = row.original;
        return (
          <GroupAvatarCell
            group={group}
            onAvatarClick={options?.onAvatarClick}
          />
        );
      },
    },
    {
      accessorKey: "name",
      header: t("Name"),
      editable: true,
      sortable: true,
      inputType: "text",
      width: 200,
    },
    {
      accessorKey: "childcare_id",
      header: t("Childcare"),
      editable: false,
      sortable: true,
      width: 150,
      cell: ({ row }: { row: { original: Group } }) => {
        const childcareId = row.original.childcare_id;
        if (!childcareId) {
          return (
            <span className="text-muted-foreground">{t("All Childcares")}</span>
          );
        }
        const childcare = options?.childcares?.find(
          (c) => c.id === childcareId
        );
        return <span>{childcare?.name || childcareId}</span>;
      },
    },
    {
      accessorKey: "sort_order",
      header: t("Sort Order"),
      editable: true,
      sortable: true,
      inputType: "number",
      width: 120,
    },
    {
      id: "labels",
      accessorKey: "labels",
      header: t("Labels"),
      editable: true,
      sortable: false,
      inputType: "tag-select",
      selectOptions: options?.availableLabels?.map(l => ({
        value: l.id,
        label: l.label,
      })) || [],
      multiSelectOptions:
        options?.availableLabels?.map((label) => ({
          value: label.id!,
          label: label.label || "",
          color: label.color,
        })) || [],
      cell: ({ row }: { row: { original: Group } }) => {
        const labels = row.original.labels || [];
        return (
          <div className="flex flex-wrap gap-1" data-testid="labels-cell">
            {labels.map((label) => (
              <Badge
                key={label.id}
                variant="secondary"
                style={{
                  backgroundColor: label.color ? `${label.color}20` : undefined,
                  borderColor: label.color || undefined,
                  color: label.color || undefined,
                }}
              >
                {label.label}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: "type",
      header: t("Type"),
      editable: true,
      sortable: true,
      inputType: "select",
      selectOptions: options?.typeOptions || [],
      width: 120,
      cell: ({ row }: { row: { original: Group } }) => {
        const type = row.original.type;
        if (!type) return null;
        const typeOption = options?.typeOptions?.find(
          (opt) => opt.value === type
        );
        return <span>{typeOption?.label || type}</span>;
      },
    },
    {
      accessorKey: "subsidy_type",
      header: t("Subsidy Type"),
      editable: true,
      sortable: true,
      inputType: "select",
      selectOptions: options?.typeOptions || [],
      width: 140,
      cell: ({ row }: { row: { original: Group } }) => {
        const subsidyType = row.original.subsidy_type;
        if (!subsidyType) return null;
        const option = options?.typeOptions?.find(
          (opt) => opt.value === subsidyType
        );
        return <span>{option?.label || subsidyType}</span>;
      },
    },
    {
      id: "age_range",
      accessorKey: "age_min",
      header: t("Age Range"),
      editable: false,
      sortable: true,
      cell: ({ row }: { row: { original: Group } }) => {
        const group = row.original;
        const min = group.age_min ?? 0;
        const max = group.age_max ?? 18;
        return (
          <div className="px-2">
            {min} - {max} {t("years")}
          </div>
        );
      },
    },
    {
      accessorKey: "max_headcount",
      header: t("Max Headcount"),
      editable: true,
      sortable: true,
      inputType: "number",
      width: 140,
    },
    {
      accessorKey: "max_weighted_headcount",
      header: t("Max Weighted"),
      editable: true,
      sortable: true,
      inputType: "number",
      width: 140,
    },
    {
      accessorKey: "created_at",
      header: t("Created At"),
      editable: false,
      sortable: true,
      inputType: "date",
    },
  ];
}

// TransformedContract column definitions
export function getContractColumns(
  t: TranslationFunction,
  _navigate?: NavigateFunction,
  options?: {
    onView?: (contract: TransformedContract) => void;
    onEdit?: (contract: TransformedContract) => void;
    onClone?: (contract: TransformedContract) => void;
    children?: Array<{
      id: string;
      firstname: string;
      lastname: string;
      childInternalId?: string;
      avatarUrl?: string;
    }>;
    // Both keys are optional on the wire (`billing_TariffVersionResponse`), and the
    // lookup below already guards on `matchingTariff?.name`.
    tariffVersions?: Array<{
      id?: string;
      name?: string;
    }>;
    availableLabels?: Array<{
      id: string;
      label: string;
      color?: string | null;
    }>;
  }
): EditableCellConfig<TransformedContract>[] {
  return [
    {
      accessorKey: "id" as keyof TransformedContract,
      id: "avatar",
      header: "",
      editable: false,
      sortable: false,
      enableHiding: false, // Avatar column not selectable in manage columns
      cell: ({ row }: { row: { original: TransformedContract } }) => {
        const contract = row.original;

        // Find the child data to display the avatar
        const child = options?.children?.find((c) => c.id === contract.childId);
        const childForAvatar = child
          ? {
              id: child.id,
              firstname: child.firstname,
              lastname: child.lastname,
              childInternalId: child.childInternalId,
              avatarUrl: child.avatarUrl || undefined,
            }
          : {
              id: contract.childId || "",
              firstname: contract.childName?.split(" ")[0] || "",
              lastname: contract.childName?.split(" ").slice(1).join(" ") || "",
              avatarUrl: undefined,
            };

        return (
          <ChildAvatarCell
            child={childForAvatar as Child}
            onClick={() => {
              if (options?.onEdit) {
                options.onEdit(contract);
              } else if (options?.onView) {
                options.onView(contract);
              }
            }}
          />
        );
      },
    },
    {
      accessorKey: "id",
      header: t("table.contracts.columns.contractNumber"),
      editable: false,
      sortable: true,
      inputType: "text",
      cell: ({ getValue }) => {
        const id = getValue() as string;
        // Display a shortened version of the ID as contract number
        return id ? id.substring(0, 8).toUpperCase() : "-";
      },
    },
    {
      accessorKey: "childInternalId",
      header: t("table.contracts.columns.childInternalId"),
      editable: false,
      sortable: true,
      inputType: "text",
      cell: ({ row }) => {
        const contract = row.original as TransformedContract & { childInternalId?: string };
        // Use childInternalId directly from the contract response (transformed from child_internal_id by ContractTransformer)
        return contract.childInternalId || "-";
      },
    },
    {
      accessorKey: "childName",
      header: t("table.contracts.columns.childName"),
      editable: false,
      sortable: true,
      inputType: "text",
      cell: ({ row }) => {
        const contract = row.original;
        const child = options?.children?.find((c) => c.id === contract.childId);

        // Get full name
        let fullName = "";
        if (child) {
          fullName = `${child.firstname || ""} ${child.lastname || ""}`.trim();
        } else if (contract.childName) {
          fullName = contract.childName;
        }

        return fullName || "-";
      },
    },
    {
      id: "labels",
      accessorKey: "labels",
      header: t("table.contracts.columns.labels"),
      editable: true,
      sortable: false,
      inputType: "tag-select",
      selectOptions: options?.availableLabels?.map(l => ({
        value: l.id,
        label: l.label || '',
      })) || [],
      multiSelectOptions:
        options?.availableLabels?.map((label) => ({
          value: label.id,
          label: label.label || "",
          color: label.color,
        })) || [],
      cell: ({ row }: { row: { original: TransformedContract } }) => {
        const labels =
          (row.original as TransformedContract & {
            labels?: Array<{
              id?: string;
              label?: string;
              color?: string | null;
            }>;
          }).labels || [];
        return (
          <div className="flex flex-wrap gap-1" data-testid="labels-cell">
            {labels.map(
              (label: {
                id?: string;
                label?: string;
                color?: string | null;
              }) => (
                <Badge
                  key={label.id}
                  variant="secondary"
                  style={{
                    backgroundColor: label.color
                      ? `${label.color}20`
                      : undefined,
                    borderColor: label.color || undefined,
                    color: label.color || undefined,
                  }}
                >
                  {label.label}
                </Badge>
              )
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "startDate",
      header: t("table.contracts.columns.startDate"),
      editable: false,
      sortable: true,
      inputType: "date",
      // The list shows one row per contract GROUP, so display the group's earliest
      // start (group_start_date) rather than the representative version's start —
      // otherwise a group whose latest version is recent looks like it began then.
      cell: ({ row }: { row: { original: TransformedContract } }) => {
        const c = row.original as TransformedContract & {
          groupStartDate?: Date | string | null;
          startDate?: Date | string | null;
        };
        return formatDate(c.groupStartDate ?? c.startDate, i18n.locale);
      },
    },
    {
      accessorKey: "endDate",
      header: t("table.contracts.columns.endDate"),
      editable: false,
      sortable: true,
      inputType: "date",
      // Group's end = the latest version's end (group_end_date); blank when the
      // group is still ongoing. Falls back to the row's own end only when group
      // data is absent (non-list responses).
      cell: ({ row }: { row: { original: TransformedContract } }) => {
        const c = row.original as TransformedContract & {
          groupStartDate?: Date | string | null;
          groupEndDate?: Date | string | null;
          endDate?: Date | string | null;
        };
        const value = c.groupStartDate != null ? c.groupEndDate : c.endDate;
        return formatDate(value, i18n.locale);
      },
    },
    {
      accessorKey: "subsidised",
      header: t("table.contracts.columns.subsidised"),
      editable: false,
      sortable: true,
      inputType: "checkbox",
      // Drives the "Subsidised" filter panel (filter type is "select"). A single
      // selected value maps to the boolean server-side filter in
      // ContractsPage.mapColumnFiltersToServerFilters:
      //   "true"  → ?subsidised=true
      //   "false" → ?subsidised=false
      selectOptions: [
        { value: "true", label: t("table.contracts.subsidised.yes") },
        { value: "false", label: t("common.no") },
      ],
      cell: ({ row }: { row: { original: TransformedContract } }) => {
        const c = row.original as TransformedContract & {
          subsidised?: boolean;
        };
        if (c.subsidised) {
          return (
            <Badge variant="outline" className="text-xs">
              {t("table.contracts.subsidised.yes")}
            </Badge>
          );
        }
        return t("common.no");
      },
    },
    // Group-level flags (#1748), hidden by default (ContractsTable). They host the
    // "Subsidy decision pending" / "Has staff notes" filters and show the same
    // predicate the server filters on: true when ANY non-deleted version of the
    // contract group matches, not only the version shown in the row.
    {
      accessorKey: "groupSubsidyDecisionPending",
      header: t("table.contracts.columns.subsidyDecisionPending"),
      editable: false,
      sortable: false,
      inputType: "checkbox",
      //   "true"  → ?subsidy_decision_pending=true
      //   "false" → ?subsidy_decision_pending=false
      selectOptions: [
        { value: "true", label: t("common.yes") },
        { value: "false", label: t("common.no") },
      ],
      cell: ({ row }: { row: { original: TransformedContract } }) =>
        row.original.groupSubsidyDecisionPending ? t("common.yes") : t("common.no"),
    },
    {
      accessorKey: "groupHasStaffNotes",
      header: t("table.contracts.columns.hasStaffNotes"),
      editable: false,
      sortable: false,
      inputType: "checkbox",
      //   "true"  → ?has_staff_notes=true
      //   "false" → ?has_staff_notes=false
      selectOptions: [
        { value: "true", label: t("common.yes") },
        { value: "false", label: t("common.no") },
      ],
      cell: ({ row }: { row: { original: TransformedContract } }) =>
        row.original.groupHasStaffNotes ? t("common.yes") : t("common.no"),
    },
    {
      accessorKey: "tariffVersions",
      header: t("table.contracts.columns.tariffVersions"),
      editable: false,
      sortable: false,
      inputType: "text",
      cell: ({ row }) => {
        const contract = row.original;
        const contractTariffVersions = contract.tariffVersions;

        // Try to show tariffName first if available
        if (contract.tariffName && contract.tariffName.trim() !== "") {
          return (
            <Badge variant="outline" className="text-xs">
              {contract.tariffName}
            </Badge>
          );
        }

        // Otherwise show tariff versions if available
        if (!contractTariffVersions || contractTariffVersions.length === 0) {
          return "-";
        }

        // Look up the tariff version names from the fetched tariff versions.
        // `TransformedContract.tariffVersions` is `Array<Record<string, unknown>>`,
        // so the callback parameter must be typed as the element type and each
        // field read through a runtime guard. The runtime values ARE camelCase:
        // `fieldTransformers.transformKeys` recurses into nested arrays/objects,
        // so the wire's `tariff_version_id`/`group_type` arrive as
        // `tariffVersionId`/`groupType`.
        const asString = (value: unknown) =>
          typeof value === "string" ? value : undefined;
        const tariffData = contractTariffVersions.map(
          (ctv: Record<string, unknown>) => {
            const tariffVersionId = asString(ctv.tariffVersionId);
            const groupType = asString(ctv.groupType);
            if (options?.tariffVersions) {
              const matchingTariff = options.tariffVersions.find(
                (tv) => tv.id === tariffVersionId
              );
              if (matchingTariff?.name) {
                return { name: matchingTariff.name, groupType };
              }
            }
            // Fallback to group type if name not found
            return {
              name: groupType || "Standard",
              groupType,
            };
          }
        );

        // Display the names as badges
        return (
          <div className="flex flex-wrap gap-1">
            {tariffData.map((tariff: { name: string }, index: number) => (
              <Badge key={index} variant="outline" className="text-xs">
                {tariff.name}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: t("table.contracts.columns.status"),
      editable: false,
      sortable: true,
      inputType: "select",
      selectOptions: [
        { value: "in_effect", label: t("common.active") },
        { value: "upcoming", label: t("common.upcoming") },
        { value: "expired", label: t("common.expired") },
        { value: "cancelled", label: t("common.cancelled") },
        { value: "terminated", label: t("common.terminated") },
      ],
      cell: ({ row }) => {
        // Dead-read repair (#1156): the contracts table renders rows camelCased by
        // `ContractTransformer.toFrontend`, so reading only `effective_status` always
        // yielded undefined and the badge fell back to the raw lifecycle `status`.
        // Both spellings are read so raw (untransformed) rows keep working.
        const effectiveStatus = (((row.original as Record<string, unknown>).effectiveStatus ??
          (row.original as Record<string, unknown>).effective_status) as string || "").toUpperCase();
        const status = ((row.original as Record<string, unknown>).status as string || "").toUpperCase();
        const canonical = (() => {
          if (effectiveStatus) return effectiveStatus;
          if (!status) return "";
          if (status === "ACTIVE") return "IN_EFFECT";
          if (status === "ARCHIVED") return "EXPIRED";
          if (status === "DRAFT" || status === "FUTURE") return "UPCOMING";
          return status;
        })();

        if (!canonical) {
          return "-";
        }

        const statusLabels: Record<string, string> = {
          IN_EFFECT: t("common.active"),
          UPCOMING: t("common.upcoming"),
          EXPIRED: t("common.expired"),
          CANCELLED: t("common.cancelled"),
          TERMINATED: t("common.terminated"),
        };
        return statusLabels[canonical] || canonical;
      },
    },
    {
      accessorKey: "createdAt",
      header: t("table.contracts.columns.createdAt"),
      editable: false,
      sortable: true,
      inputType: "date",
    },
  ];
}

export function getLabelColumns(
  t: TranslationFunction,
  options?: {
    onAvatarClick?: (label: Label) => void;
    onEdit?: (label: Label) => void;
  }
): EditableCellConfig<Label>[] {
  return [
    {
      id: "searchableContent",
      accessorKey: "searchableContent" as keyof Label,
      header: "",
      editable: false,
      sortable: false,
      enableHiding: true,
      cell: () => null, // Hidden column for search
    },
    {
      id: "avatar",
      accessorKey: "id" as keyof Label,
      header: "",
      editable: false,
      sortable: false,
      width: 80,
      enableHiding: true,
      cell: ({ row }: { row: { original: Label } }) => {
        const label = row.original;
        return (
          <LabelAvatarCell
            label={label}
            onAvatarClick={options?.onAvatarClick}
          />
        );
      },
    },
    {
      accessorKey: "label",
      header: t("Label"),
      editable: false,
      sortable: true,
      inputType: "text",
      width: 200,
      cell: ({ row }) => {
        const label = row.original;
        return (
          <Badge
            variant="secondary"
            style={{
              backgroundColor: label.color ? `${label.color}20` : undefined,
              borderColor: label.color || undefined,
              color: label.color || undefined,
            }}
          >
            {label.label}
          </Badge>
        );
      },
    },
    {
      accessorKey: "entity_type",
      header: t("Entity Type"),
      editable: false,
      sortable: true,
      inputType: "select",
      width: 120,
      selectOptions: [
        { value: "all", label: t("All Types") },
        { value: "groups", label: t("Groups") },
        { value: "children", label: t("Children") },
        { value: "employees", label: t("Employees") },
      ],
      formatter: (value: string) => {
        switch (value) {
          case "all":
            return t("All Types");
          case "groups":
            return t("Groups");
          case "children":
            return t("Children");
          case "employees":
            return t("Employees");
          default:
            return value;
        }
      },
    },
    {
      accessorKey: "description",
      header: t("Description"),
      editable: true,
      sortable: false,
      inputType: "textarea",
      width: 300,
    },
    {
      accessorKey: "created_at",
      header: t("Created At"),
      editable: false,
      sortable: true,
      inputType: "date",
      width: 150,
    },
  ];
}

// Contact column definitions
export function getContactColumns(
  t: TranslationFunction,
  navigate?: NavigateFunction,
  enumOptions?: {
    status?: { value: string; label: string }[];
    gender?: { value: string; label: string }[];
  },
  _options?: { availableLabels?: Array<{ id?: string; label?: string; color?: string }> }
): EditableCellConfig<Contact>[] {
  return [
    {
      id: "avatar",
      accessorKey: "id" as keyof Contact,
      header: "",
      editable: false,
      sortable: false,
      enableHiding: true,
      cell: ({ row }: { row: { original: Contact } }) => {
        const contact = row.original;
        return (
          <ContactAvatarCell
            contact={contact}
            onClick={() => {
              if (navigate) {
                navigate(`/contacts/${contact.id}/edit`);
              }
            }}
          />
        );
      },
    },
    {
      accessorKey: "contactInternalId",
      header: t("Internal ID"),
      editable: true,
      sortable: true,
      inputType: "text",
    },
    {
      accessorKey: "firstname",
      header: t("First Name"),
      // See getChildrenColumns: a link on the list, editable in the preview.
      editable: !navigate,
      sortable: true,
      inputType: "text",
      ...(navigate && {
        cell: ({ row }: { row: { original: Contact } }) =>
          row.original.id ? (
            <RecordNameLink to={`/contacts/${row.original.id}/edit`}>
              {row.original.firstname}
            </RecordNameLink>
          ) : (
            row.original.firstname
          ),
      }),
    },
    {
      id: "labels",
      accessorKey: "labels",
      header: t("Labels"),
      editable: false,
      sortable: false,
      selectOptions: _options?.availableLabels?.map(l => ({
        value: l.id || '',
        label: l.label || '',
      })) || [],
      cell: ({ row }: { row: { original: Contact } }) => {
        const labels = row.original.labels || [];
        return (
          <div className="flex flex-wrap gap-1" data-testid="labels-cell">
            {labels.map((label: { id?: string; label?: string; color?: string | null }) => (
              <Badge
                key={label.id}
                variant="secondary"
                style={{
                  backgroundColor: label.color ? `${label.color}20` : undefined,
                  borderColor: label.color || undefined,
                  color: label.color || undefined,
                }}
              >
                {label.label}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: "lastname",
      header: t("Last Name"),
      editable: true,
      sortable: true,
      inputType: "text",
    },
    {
      accessorKey: "emailMain",
      header: t("Email Main"),
      editable: true,
      sortable: true,
      inputType: "email",
    },
    {
      accessorKey: "telMobile",
      header: t("Mobile Phone"),
      editable: true,
      sortable: true,
      inputType: "text",
      cell: ({ row }: { row: { original: Contact } }) => {
        const phone = row.original.telMobile;
        const countryCode = (row.original as Record<string, unknown>).telMobileCountryCode as string | undefined;
        if (!phone) return null;
        return formatPhoneWithCountryCode(phone, countryCode) ?? phone;
      },
    },
    {
      accessorKey: "gender",
      header: t("Gender"),
      editable: true,
      sortable: true,
      inputType: "select",
      selectOptions: enumOptions?.gender || [
        {
          label: t("children.enums.gender.male"),
          value: models_GenderType.GenderMale,
        },
        {
          label: t("children.enums.gender.female"),
          value: models_GenderType.GenderFemale,
        },
        {
          label: t("children.enums.gender.other"),
          value: models_GenderType.GenderOther,
        },
        {
          label: t("children.enums.gender.unknown"),
          value: models_GenderType.GenderUnknown,
        },
      ],
      cell: ({ row }: { row: { original: Contact } }) => {
        const gender = row.original.gender;
        if (!gender) return null;
        const option = (
          enumOptions?.gender || [
            {
              label: t("children.enums.gender.male"),
              value: models_GenderType.GenderMale,
            },
            {
              label: t("children.enums.gender.female"),
              value: models_GenderType.GenderFemale,
            },
            {
              label: t("children.enums.gender.other"),
              value: models_GenderType.GenderOther,
            },
            {
              label: t("children.enums.gender.unknown"),
              value: models_GenderType.GenderUnknown,
            },
          ]
        ).find((opt) => opt.value === gender);
        return <span>{option?.label || gender}</span>;
      },
    },
    {
      accessorKey: "birthdate",
      header: t("Birth Date"),
      editable: true,
      sortable: true,
      inputType: "date",
      cell: ({ row }: { row: { original: Contact } }) => {
        const date = row.original.birthdate;
        if (!date) return null;
        return formatDate(date, i18n.locale);
      },
    },
    {
      accessorKey: "customerScore",
      header: t("contacts.fields.customerScore"),
      editable: true,
      sortable: true,
      width: 150,
      cell: ({ row }: { row: { original: Contact } }) => {
        return <ScoreCell score={row.original.customerScore} />;
      },
    },
    {
      accessorKey: "status",
      header: t("Status"),
      // Derived by the entity-status derivation job (issue #356) — not hand-editable.
      editable: false,
      sortable: true,
      inputType: "select",
      selectOptions: enumOptions?.status || [
        {
          label: t("Active"),
          value: models_EntityStatus.StatusActive,
        },
        {
          label: t("Inactive"),
          value: models_EntityStatus.StatusInactive,
        },
        {
          label: t("Upcoming"),
          value: models_EntityStatus.StatusUpcoming,
        },
      ],
    },
    {
      accessorKey: "createdAt",
      header: t("Created At"),
      editable: false,
      sortable: true,
      inputType: "date",
    },
  ];
}

export function getChildcareColumns(
  t: TranslationFunction,
  options?: {
    onAvatarClick?: (childcare: Childcare) => void;
    onEdit?: (childcare: Childcare) => void;
  }
): EditableCellConfig<Childcare>[] {
  return [
    {
      id: "searchableContent",
      accessorKey: "searchableContent" as keyof Childcare,
      header: "",
      editable: false,
      sortable: false,
      enableHiding: true,
      cell: () => null, // Hidden column for search
    },
    {
      id: "avatar",
      accessorKey: "id" as keyof Childcare,
      header: "",
      editable: false,
      sortable: false,
      width: 80,
      enableHiding: true,
      cell: ({ row }: { row: { original: Childcare } }) => {
        const childcare = row.original;
        // Use logo_url which contains either the image ID or direct URL
        return (
          <BaseAvatarCell
            entity={{
              firstname: childcare.name || "",
              lastname: "",
              avatarUrl: childcare.logo_url || undefined,
            }}
            onClick={() => options?.onAvatarClick?.(childcare)}
          />
        );
      },
    },
    {
      accessorKey: "name",
      header: t("Name"),
      editable: true,
      sortable: true,
      inputType: "text",
      width: 200, // Fixed width to prevent flickering
    },
    {
      id: "address",
      accessorKey: "address_id",
      header: t("Address"),
      editable: false,
      sortable: false,
      cell: ({ row }: { row: { original: Childcare } }) => {
        return (
          <div className="px-2">
            <AddressDisplay
              addressId={row.original.address_id}
              iconPosition="right"
            />
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: t("Created At"),
      editable: false,
      sortable: true,
      inputType: "date",
    },
  ];
}

// Activity column definitions
export function getActivityColumns(
  t: TranslationFunction,
  navigate?: NavigateFunction,
  options?: {
    activityTypeOptions?: { value: string; label: string }[];
    onActivityClick?: (activityId: string) => void;
  }
): EditableCellConfig<ActivityResponse>[] {
  return [
    {
      accessorKey: "id" as keyof ActivityResponse,
      id: "avatar",
      header: "",
      editable: false,
      sortable: false,
      enableHiding: false, // Avatar column always visible
      cell: ({ row }: { row: { original: ActivityResponse } }) => {
        const activity = row.original;
        const child = activity.child;

        if (!child) {
          return <span className="text-muted-foreground">-</span>;
        }

        // Build child object for ChildAvatarCell
        const childForAvatar = {
          id: child.id || "",
          firstname: child.firstname || "",
          lastname: child.lastname || "",
          childInternalId: child.internal_id,
          avatarUrl: child.avatar_url || undefined,
        };

        return (
          <ChildAvatarCell
            child={childForAvatar as Child}
            onClick={() => {
              // If onActivityClick is provided, open activity modal
              // Otherwise, navigate to child edit page (legacy behavior)
              if (options?.onActivityClick && activity.id) {
                options.onActivityClick(activity.id);
              } else if (navigate && child.id) {
                navigate(`/children/${child.id}/edit`);
              }
            }}
          />
        );
      },
    },
    {
      accessorKey: "child" as keyof ActivityResponse,
      id: "child_name",
      header: t("table.activities.columns.childName"),
      editable: false,
      sortable: true,
      cell: ({ row }: { row: { original: ActivityResponse } }) => {
        const child = row.original.child;
        if (!child) return <span className="text-muted-foreground">-</span>;
        const name = `${child.firstname || ""} ${child.lastname || ""}`.trim();
        return <span>{name || "-"}</span>;
      },
    },
    {
      accessorKey: "type" as keyof ActivityResponse,
      id: "type",
      header: t("table.activities.columns.type"),
      editable: false,
      sortable: true,
      inputType: "select",
      selectOptions: options?.activityTypeOptions || [],
      cell: ({ row }: { row: { original: ActivityResponse } }) => {
        const type = row.original.type;
        if (!type) return <span className="text-muted-foreground">-</span>;
        const labels = getActivityTypeLabels(i18n);
        const label = labels[type];
        return <span>{label || type}</span>;
      },
    },
    {
      accessorKey: "activity_time" as keyof ActivityResponse,
      id: "activity_time",
      header: t("table.activities.columns.activityTime"),
      editable: false,
      sortable: true,
      inputType: "date",
      cell: ({ row }: { row: { original: ActivityResponse } }) => {
        const time = row.original.activity_time;
        if (!time) return <span className="text-muted-foreground">-</span>;
        return <span>{formatDateTime(time)}</span>;
      },
    },
    {
      accessorKey: "recorded_by" as keyof ActivityResponse,
      id: "recorded_by",
      header: t("table.activities.columns.recordedBy"),
      editable: false,
      sortable: true,
      cell: ({ row }: { row: { original: ActivityResponse } }) => {
        const recordedBy = row.original.recorded_by;
        if (!recordedBy) return <span className="text-muted-foreground">-</span>;
        const name = `${recordedBy.first_name || ""} ${recordedBy.last_name || ""}`.trim();
        return <span>{name || "-"}</span>;
      },
    },
    {
      accessorKey: "created_at" as keyof ActivityResponse,
      id: "created_at",
      header: t("table.activities.columns.createdAt"),
      editable: false,
      sortable: true,
      inputType: "date",
      cell: ({ row }: { row: { original: ActivityResponse } }) => {
        const createdAt = row.original.created_at;
        if (!createdAt) return <span className="text-muted-foreground">-</span>;
        return <span>{formatDate(createdAt, i18n.locale)}</span>;
      },
    },
  ];
}

// Employee Absence column definitions
/**
 * Counts the days in an inclusive [from, to] absence range for display.
 *
 * For a CALENDAR_DAYS basis every day in the range is counted; for a
 * WORK_DAYS basis only weekdays (Mon–Fri) are counted. This is a display-only
 * approximation (it has no employee-schedule data) used solely to render the
 * counting-basis hint in the absence table.
 *
 * @returns The day count, or null when either date is missing/invalid.
 */
function countAbsenceDays(
  fromDate: string | undefined,
  toDate: string | undefined,
  basis: "WORK_DAYS" | "CALENDAR_DAYS"
): number | null {
  if (!fromDate || !toDate) return null;
  const from = new Date(`${fromDate}T00:00:00`);
  const to = new Date(`${toDate}T00:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  if (to < from) return null;

  let count = 0;
  const cursor = new Date(from);
  while (cursor <= to) {
    if (basis === "CALENDAR_DAYS") {
      count += 1;
    } else {
      const day = cursor.getDay();
      // 0 = Sunday, 6 = Saturday
      if (day !== 0 && day !== 6) count += 1;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

/** Row actions on a self-reported absence (#1767). Every handler is optional. */
export interface AbsenceReportActionHandlers {
  onAcknowledge?: (absence: EmployeeAbsence) => void;
  onCancelReport?: (absence: EmployeeAbsence) => void;
  onEauRetrieved?: (absence: EmployeeAbsence) => void;
  onEditDates?: (absence: EmployeeAbsence) => void;
  onViewCertificates?: (absence: EmployeeAbsence) => void;
}

export interface AbsenceReportActionOptions extends AbsenceReportActionHandlers {
  /** The organisation's certificate mode; "eAU retrieved" exists only in EAU mode. */
  certificateMode?: CertificateModeValue;
  /** Admin or `employees` holder: cancel, eAU retrieved and edit dates. */
  canWrite?: boolean;
  /** Admin or `employees` holder: the certificate viewer. */
  canReadCertificates?: boolean;
}

export type AbsenceReportActionKey =
  | "acknowledge"
  | "cancelReport"
  | "eauRetrieved"
  | "editDates"
  | "viewCertificates";

/** True when at least one report action handler is wired (D27: else no actions column). */
export function hasAbsenceReportActionHandlers(
  options: AbsenceReportActionHandlers | undefined
): boolean {
  return Boolean(
    options?.onAcknowledge ||
      options?.onCancelReport ||
      options?.onEauRetrieved ||
      options?.onEditDates ||
      options?.onViewCertificates
  );
}

/**
 * The actions a row offers, in menu order. Only self-reports have actions;
 * a request or an office-created absence has none.
 */
export function getAbsenceReportActions(
  absence: EmployeeAbsence,
  options: AbsenceReportActionOptions | undefined
): AbsenceReportActionKey[] {
  if (!absence.is_self_report || !options) return [];
  const active = absence.approval_status === ApprovalStatus.APPROVED;
  const canWrite = options.canWrite === true;
  const actions: AbsenceReportActionKey[] = [];

  if (options.onAcknowledge && active && !absence.acknowledged_at) {
    actions.push("acknowledge");
  }
  if (options.onEditDates && active && canWrite) {
    actions.push("editDates");
  }
  if (
    options.onEauRetrieved &&
    active &&
    canWrite &&
    options.certificateMode === CertificateMode.EAU &&
    !absence.eau_retrieved_at
  ) {
    actions.push("eauRetrieved");
  }
  if (options.onViewCertificates && options.canReadCertificates === true) {
    actions.push("viewCertificates");
  }
  if (options.onCancelReport && active && canWrite) {
    actions.push("cancelReport");
  }
  return actions;
}

function renderAbsenceReportActionItem(
  action: AbsenceReportActionKey,
  absence: EmployeeAbsence,
  options: AbsenceReportActionOptions
) {
  switch (action) {
    case "acknowledge":
      return (
        <DropdownMenuItem key={action} onClick={() => options.onAcknowledge?.(absence)}>
          <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden="true" />
          {i18n._({ id: "absences.action.acknowledge", message: "Acknowledge" })}
        </DropdownMenuItem>
      );
    case "editDates":
      return (
        <DropdownMenuItem key={action} onClick={() => options.onEditDates?.(absence)}>
          <CalendarRange className="mr-2 h-4 w-4" aria-hidden="true" />
          {i18n._({ id: "absences.action.editDates", message: "Edit dates" })}
        </DropdownMenuItem>
      );
    case "eauRetrieved":
      return (
        <DropdownMenuItem key={action} onClick={() => options.onEauRetrieved?.(absence)}>
          <FileCheck className="mr-2 h-4 w-4" aria-hidden="true" />
          {i18n._({ id: "absences.action.eauRetrieved", message: "eAU retrieved" })}
        </DropdownMenuItem>
      );
    case "viewCertificates":
      return (
        <DropdownMenuItem key={action} onClick={() => options.onViewCertificates?.(absence)}>
          <FileText className="mr-2 h-4 w-4" aria-hidden="true" />
          {i18n._({ id: "absences.action.viewCertificates", message: "Certificates" })}
        </DropdownMenuItem>
      );
    case "cancelReport":
      return (
        <DropdownMenuItem
          key={action}
          className="text-destructive focus:text-destructive"
          onClick={() => options.onCancelReport?.(absence)}
        >
          <XCircle className="mr-2 h-4 w-4" aria-hidden="true" />
          {i18n._({ id: "absences.action.cancelReport", message: "Cancel report" })}
        </DropdownMenuItem>
      );
    default:
      return null;
  }
}

export function getAbsenceColumns(
  t: TranslationFunction,
  options?: {
    onAvatarClick?: (absence: EmployeeAbsence) => void;
    absenceTypeOptions?: { value: string; label: string }[];
    reportActions?: AbsenceReportActionOptions;
  }
): EditableCellConfig<EmployeeAbsence>[] {
  const reportActions = options?.reportActions;
  // D27: the actions column exists only when a caller wires at least one handler.
  const actionsColumn: EditableCellConfig<EmployeeAbsence>[] =
    reportActions && hasAbsenceReportActionHandlers(reportActions)
      ? [
          {
            accessorKey: "id" as keyof EmployeeAbsence,
            id: "actions",
            header: t(defineMessage({ id: "absences.action.menu", message: "Actions" })),
            editable: false,
            sortable: false,
            width: 80,
            cell: ({ row }: { row: { original: EmployeeAbsence } }) => {
              const absence = row.original;
              const actions = getAbsenceReportActions(absence, reportActions);
              if (actions.length === 0) return null;
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="h-8 w-8 p-0"
                      aria-label={i18n._({ id: "absences.action.menu", message: "Actions" })}
                    >
                      <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {actions.map((action) =>
                      renderAbsenceReportActionItem(action, absence, reportActions)
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            },
          },
        ]
      : [];

  return [
    {
      accessorKey: "id" as keyof EmployeeAbsence,
      id: "avatar",
      header: "",
      editable: false,
      sortable: false,
      enableHiding: false, // Avatar column always visible
      cell: ({ row }: { row: { original: EmployeeAbsence } }) => {
        const absence = row.original;
        const employee = absence.employee;

        if (!employee) {
          return <span className="text-muted-foreground">-</span>;
        }

        // Build employee object for BaseAvatarCell
        const employeeForAvatar = {
          firstname: employee.firstname || "",
          lastname: employee.lastname || "",
          avatarUrl: employee.avatar_url || undefined,
        };

        return (
          <BaseAvatarCell
            entity={employeeForAvatar}
            onClick={() => options?.onAvatarClick?.(absence)}
          />
        );
      },
    },
    {
      accessorKey: "employee" as keyof EmployeeAbsence,
      id: "employee_name",
      header: t("Employee"),
      editable: false,
      sortable: true,
      cell: ({ row }: { row: { original: EmployeeAbsence } }) => {
        const employee = row.original.employee;
        if (!employee) return <span className="text-muted-foreground">-</span>;
        const name = `${employee.firstname || ""} ${employee.lastname || ""}`.trim();
        return <span className="font-medium">{name || "-"}</span>;
      },
    },
    {
      accessorKey: "absence_type" as keyof EmployeeAbsence,
      id: "absence_type",
      header: t("Type"),
      editable: false,
      sortable: true,
      inputType: "select",
      selectOptions: options?.absenceTypeOptions || [],
      width: 100,
      cell: ({ row }: { row: { original: EmployeeAbsence } }) => {
        const absence = row.original;
        const type = absence.absence_type as AbsenceTypeValue;
        if (!type) return <span className="text-muted-foreground">-</span>;

        const dayCount = countAbsenceDays(
          absence.from_date,
          absence.to_date,
          absenceCountBasis(type)
        );

        return (
          <div className="flex flex-col gap-0.5">
            <div className="flex flex-wrap items-center gap-1">
              <AbsenceTypeBadge type={type} />
              {absence.is_self_report && (
                <Badge variant="outline" data-testid="absence-report-marker">
                  {i18n._({ id: "absences.marker.report", message: "Report" })}
                </Badge>
              )}
            </div>
            {absence.overlaps_vacation && (
              <span
                className="flex items-center gap-1 text-xs text-warning-on-surface"
                data-testid="absence-overlaps-vacation"
              >
                <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
                {i18n._({
                  id: "absences.marker.overlapsVacation",
                  message: "Overlaps vacation",
                })}
              </span>
            )}
            {dayCount !== null && (
              <span className="text-xs text-muted-foreground">
                {absenceCountLabel(dayCount, type, i18n)}
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "from_date" as keyof EmployeeAbsence,
      id: "from_date",
      header: t("From"),
      editable: false,
      sortable: true,
      inputType: "date",
      width: 120,
      cell: ({ row }: { row: { original: EmployeeAbsence } }) => {
        const absence = row.original;
        const date = absence.from_date;
        if (!date) return <span className="text-muted-foreground">-</span>;

        const formattedDate = formatDate(date, i18n.locale);
        const time = !absence.full_day && absence.from_time ? absence.from_time : null;

        return (
          <div className="flex flex-col">
            <span>{formattedDate}</span>
            {time && (
              <span className="text-xs text-muted-foreground">{time}</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "to_date" as keyof EmployeeAbsence,
      id: "to_date",
      header: t("To"),
      editable: false,
      sortable: true,
      inputType: "date",
      width: 120,
      cell: ({ row }: { row: { original: EmployeeAbsence } }) => {
        const absence = row.original;
        const date = absence.to_date;
        if (!date) return <span className="text-muted-foreground">-</span>;

        const formattedDate = formatDate(date, i18n.locale);
        const time = !absence.full_day && absence.to_time ? absence.to_time : null;

        return (
          <div className="flex flex-col">
            <span>{formattedDate}</span>
            {time && (
              <span className="text-xs text-muted-foreground">{time}</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "full_day" as keyof EmployeeAbsence,
      id: "full_day",
      header: t("Full Day"),
      editable: false,
      sortable: true,
      width: 90,
      cell: ({ row }: { row: { original: EmployeeAbsence } }) => {
        const fullDay = row.original.full_day;
        return (
          <Badge variant={fullDay ? "default" : "secondary"}>
            {fullDay ? t("Yes") : t("No")}
          </Badge>
        );
      },
    },
    {
      accessorKey: "pattern_id" as keyof EmployeeAbsence,
      id: "recurring",
      header: t("Recurring"),
      editable: false,
      sortable: true,
      width: 100,
      cell: ({ row }: { row: { original: EmployeeAbsence } }) => {
        const absence = row.original;
        if (!absence.pattern_id) {
          return <span className="text-muted-foreground">-</span>;
        }
        if (absence.is_exception) {
          return (
            <Badge variant="warning" className="flex items-center gap-1">
              <Pencil className="h-3 w-3" aria-hidden="true" />
              {i18n._({ id: "absences.column.modified", message: "Modified" })}
            </Badge>
          );
        }
        return (
          <Badge variant="secondary" className="flex items-center gap-1">
            <Repeat className="h-3 w-3" />
            {i18n._({ id: "absences.column.series", message: "Series" })}
          </Badge>
        );
      },
    },
    {
      accessorKey: "notes" as keyof EmployeeAbsence,
      id: "notes",
      header: t("Notes"),
      editable: false,
      sortable: false,
      cell: ({ row }: { row: { original: EmployeeAbsence } }) => {
        const notes = row.original.notes;
        if (!notes) return <span className="text-muted-foreground">-</span>;
        return (
          <span className="max-w-xs truncate block" title={notes}>
            {notes}
          </span>
        );
      },
    },
    {
      accessorKey: "certificate_status" as keyof EmployeeAbsence,
      id: "certificate_status",
      header: t(defineMessage({ id: "absences.column.certificate", message: "Certificate" })),
      editable: false,
      sortable: true,
      width: 120,
      cell: ({ row }: { row: { original: EmployeeAbsence } }) => {
        const absence = row.original;
        // Only a report has a certificate state; every other row is NOT_REQUIRED (D4).
        if (!absence.is_self_report) {
          return <span className="text-muted-foreground">-</span>;
        }
        return (
          <CertificateStatusBadge
            status={absence.certificate_status ?? CertificateStatus.NOT_REQUIRED}
          />
        );
      },
    },
    ...actionsColumn,
  ];
}

// Course column definitions
import type { Course, CoursePlan, Attendance } from "@/types/course";
import { CourseAvatarCell, CoursePlanAvatarCell } from "./avatarColumnHelpers";
import { formatMoney, minorToMajor } from "@/lib/money";


export function getCourseColumns(
  t: TranslationFunction,
  options?: {
    onAvatarClick?: (course: Course) => void;
    statusOptions?: { value: string; label: string }[];
    instructorSelectOptions?: { value: string; label: string }[];
    currency?: string;
    enhancedEmployeeOptions?: { value: string; label: string }[];
    renderEmployeeOption?: (option: { value: string; label: string }) => React.ReactNode;
    /**
     * Rendered for an instructor id without a name when the session cannot
     * read the employee list (#1670); `undefined` keeps the dash.
     */
    instructorNameUnavailable?: React.ReactNode;
  }
): EditableCellConfig<Course>[] {
  return [
    {
      accessorKey: "id" as keyof Course,
      id: "avatar",
      header: "",
      editable: false,
      sortable: false,
      enableHiding: false,
      cell: ({ row }: { row: { original: Course } }) => {
        const course = row.original;
        return (
          <CourseAvatarCell
            course={course}
            onAvatarClick={options?.onAvatarClick}
          />
        );
      },
    },
    {
      accessorKey: "name" as keyof Course,
      id: "name",
      header: t("Name"),
      editable: true,
      sortable: true,
      inputType: "text",
      cell: ({ row }: { row: { original: Course } }) => {
        return <span className="font-medium">{row.original.name || "-"}</span>;
      },
    },
    {
      accessorKey: "price_per_unit" as keyof Course,
      id: "price_per_unit",
      header: t("Price per Session"),
      editable: true,
      sortable: true,
      inputType: "number",
      width: 140,
      // Course prices are stored in minor currency units (e.g., 2500 for 25.00 in major units)
      // Convert from minor units to major units for editing
      getEditValue: (value: number | null | undefined) => {
        if (value == null) return "";
        return minorToMajor(value).toFixed(2);
      },
      cell: ({ row }: { row: { original: Course } }) => {
        const priceMinor = row.original.price_per_unit;
        if (priceMinor == null) return <span className="text-muted-foreground">-</span>;
        // Convert from minor units (API) to major units (display)
        const priceMajor = minorToMajor(priceMinor);
        return <span>{formatMoney(priceMajor, true, options?.currency || "CHF")}</span>;
      },
    },
    {
      accessorKey: "price_per_month" as keyof Course,
      id: "price_per_month",
      header: t("Price per Month"),
      editable: true,
      sortable: true,
      inputType: "number",
      width: 140,
      // Course prices are stored in minor currency units (e.g., 10000 for 100.00 in major units)
      // Convert from minor units to major units for editing
      getEditValue: (value: number | null | undefined) => {
        if (value == null) return "";
        return minorToMajor(value).toFixed(2);
      },
      cell: ({ row }: { row: { original: Course } }) => {
        const priceMinor = row.original.price_per_month;
        if (priceMinor == null) return <span className="text-muted-foreground">-</span>;
        // Convert from minor units (API) to major units (display)
        const priceMajor = minorToMajor(priceMinor);
        return <span>{formatMoney(priceMajor, true, options?.currency || "CHF")}</span>;
      },
    },
    {
      accessorKey: "instructor_id" as keyof Course,
      id: "instructor_id",
      header: t("Instructor"),
      editable: false,
      sortable: true,
      width: 200,
      cell: ({ row }: { row: { original: Course } }) => {
        const course = row.original;
        if (course.instructor_firstname || course.instructor_lastname) {
          return (
            <span>
              {`${course.instructor_firstname ?? ""} ${course.instructor_lastname ?? ""}`.trim()}
            </span>
          );
        }
        if (course.instructor_id && options?.instructorNameUnavailable) {
          return options.instructorNameUnavailable;
        }
        return <span className="text-muted-foreground">-</span>;
      },
    },
    {
      accessorKey: "plan_count" as keyof Course,
      id: "plan_count",
      header: t("Plans"),
      editable: false,
      sortable: true,
      inputType: "number",
      width: 80,
      cell: ({ row }: { row: { original: Course } }) => {
        return <span>{row.original.plan_count ?? 0}</span>;
      },
    },
    {
      // Using "status" as accessorKey to work with the computed status field for filtering
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      accessorKey: "status" as any,
      id: "status",
      header: t("Status"),
      editable: false,
      sortable: true,
      inputType: "select",
      selectOptions: options?.statusOptions || [
        { value: "active", label: t("Active") },
        { value: "archived", label: t("Archived") },
      ],
      width: 100,
      cell: ({ row }: { row: { original: Course } }) => {
        const status = row.original.archived_at ? "archived" : "active";
        return (
          <StatusBadge
            tone={ARCHIVE_STATUS_TONE[status]}
            label={status === "archived" ? t("Archived") : t("Active")}
          />
        );
      },
    },
  ];
}

// Course Plan column definitions
const DAYS_OF_WEEK = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export function getCoursePlanColumns(
  t: TranslationFunction,
  options?: {
    onAvatarClick?: (plan: CoursePlan) => void;
    statusOptions?: { value: string; label: string }[];
  }
): EditableCellConfig<CoursePlan>[] {
  return [
    {
      accessorKey: "id" as keyof CoursePlan,
      id: "avatar",
      header: "",
      editable: false,
      sortable: false,
      enableHiding: false,
      cell: ({ row }: { row: { original: CoursePlan } }) => {
        const plan = row.original;
        return (
          <CoursePlanAvatarCell
            plan={plan}
            onAvatarClick={options?.onAvatarClick}
          />
        );
      },
    },
    {
      accessorKey: "course_name" as keyof CoursePlan,
      id: "course_name",
      header: t("Course"),
      editable: false,
      sortable: true,
      cell: ({ row }: { row: { original: CoursePlan } }) => {
        return <span className="font-medium">{row.original.course_name || "-"}</span>;
      },
    },
    {
      accessorKey: "group_names" as keyof CoursePlan,
      id: "group_names",
      header: t("Group"),
      editable: false,
      sortable: true,
      width: 150,
      cell: ({ row }: { row: { original: CoursePlan } }) => {
        const names = row.original.group_names;
        return <span>{names && names.length > 0 ? names.join(', ') : t("All Groups")}</span>;
      },
    },
    {
      accessorKey: "day_of_week" as keyof CoursePlan,
      id: "schedule",
      header: t("Schedule"),
      editable: false,
      sortable: true,
      width: 150,
      cell: ({ row }: { row: { original: CoursePlan } }) => {
        const plan = row.original;
        const parts: string[] = [];
        if (plan.day_of_week !== undefined && plan.day_of_week !== null) {
          parts.push(DAYS_OF_WEEK[plan.day_of_week] || "");
        }
        if (plan.start_time && plan.end_time) {
          parts.push(`${plan.start_time} - ${plan.end_time}`);
        }
        return <span>{parts.join(" ") || "-"}</span>;
      },
    },
    {
      accessorKey: "recurrence_rule" as keyof CoursePlan,
      id: "recurrence_rule",
      header: t("Recurrence"),
      editable: false,
      sortable: true,
      inputType: "select",
      width: 100,
      cell: ({ row }: { row: { original: CoursePlan } }) => {
        const rule = row.original.recurrence_rule;
        switch (rule) {
          case "weekly":
            return <span>{t("Weekly")}</span>;
          case "biweekly":
            return <span>{t("Bi-weekly")}</span>;
          case "monthly":
            return <span>{t("Monthly")}</span>;
          case "once":
            return <span>{t("One-time")}</span>;
          default:
            return <span className="text-muted-foreground">-</span>;
        }
      },
    },
    {
      // Using "status" as accessorKey to work with the computed status field for filtering
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      accessorKey: "status" as any,
      id: "status",
      header: t("Status"),
      editable: true,
      sortable: true,
      inputType: "select",
      selectOptions: options?.statusOptions || [
        { value: "active", label: t("Active") },
        { value: "archived", label: t("Archived") },
      ],
      width: 90,
      cell: ({ row }: { row: { original: CoursePlan } }) => {
        const status = row.original.archived_at ? "archived" : "active";
        return (
          <StatusBadge
            tone={ARCHIVE_STATUS_TONE[status]}
            label={status === "archived" ? t("Archived") : t("Active")}
          />
        );
      },
    },
  ];
}

// Attendance column definitions
export function getAttendanceColumns(
  t: TranslationFunction,
  options?: {
    onAvatarClick?: (attendance: Attendance) => void;
    billingTypeOptions?: { value: string; label: string }[];
    currency?: string;
  }
): EditableCellConfig<Attendance>[] {
  return [
    {
      accessorKey: "id" as keyof Attendance,
      id: "avatar",
      header: "",
      editable: false,
      sortable: false,
      enableHiding: false,
      cell: ({ row }: { row: { original: Attendance } }) => {
        const attendance = row.original;
        // Build child object for ChildAvatarCell (same pattern as activities table)
        const childForAvatar = {
          id: attendance.child_id || "",
          firstname: attendance.child_firstname || "",
          lastname: attendance.child_lastname || "",
          avatarUrl: attendance.child_avatar_url || undefined,
        };

        return (
          <ChildAvatarCell
            child={childForAvatar as Child}
            onClick={() => options?.onAvatarClick?.(attendance)}
          />
        );
      },
    },
    {
      accessorKey: "attendance_date" as keyof Attendance,
      id: "attendance_date",
      header: t("Date"),
      editable: false,
      sortable: true,
      inputType: "date",
      width: 120,
      cell: ({ row }: { row: { original: Attendance } }) => {
        const date = row.original.attendance_date;
        if (!date) return <span className="text-muted-foreground">-</span>;
        return <span>{formatDate(date, i18n.locale)}</span>;
      },
    },
    {
      accessorKey: "child_firstname" as keyof Attendance,
      id: "child_name",
      header: t("Child"),
      editable: false,
      sortable: true,
      cell: ({ row }: { row: { original: Attendance } }) => {
        const attendance = row.original;
        const name = `${attendance.child_firstname || ""} ${attendance.child_lastname || ""}`.trim();
        return <span className="font-medium">{name || "-"}</span>;
      },
    },
    {
      accessorKey: "course_name" as keyof Attendance,
      id: "course_plan",
      header: t("Course Plan"),
      editable: false,
      sortable: true,
      width: 200,
      cell: ({ row }: { row: { original: Attendance } }) => {
        const attendance = row.original;
        return (
          <div className="flex flex-col">
            <span className="font-medium">{attendance.course_name || "-"}</span>
            {attendance.schedule_display && (
              <span className="text-xs text-muted-foreground">
                {attendance.schedule_display}
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "enrollment_billing_type" as keyof Attendance,
      id: "billing_type",
      header: t("Billing"),
      editable: false,
      sortable: true,
      inputType: "select",
      selectOptions: options?.billingTypeOptions || [
        { value: "per_unit", label: t("Per Session") },
        { value: "per_month", label: t("Per Month") },
        { value: "free", label: t("Free") },
      ],
      width: 160,
      cell: ({ row }: { row: { original: Attendance } }) => {
        // Use override billing_type if present, otherwise fall back to enrollment default.
        // `courses_AttendanceResponse.billing_type` and `.enrollment_billing_type`
        // are two DISTINCT generated enums with identical values
        // (`per_month`/`per_unit`/`free`), so TypeScript sees no overlap even
        // though the runtime comparison is correct. Widen one side to `string`
        // for the comparison; behaviour is unchanged.
        const overrideBillingType = row.original.billing_type;
        const enrollmentBillingType: string | undefined =
          row.original.enrollment_billing_type;
        const effectiveType = overrideBillingType ?? enrollmentBillingType;
        const isOverridden =
          overrideBillingType != null && overrideBillingType !== enrollmentBillingType;

        const pricePerUnit = row.original.price_per_unit;
        const pricePerMonth = row.original.price_per_month;
        const currency = options?.currency || "CHF";

        // Course prices are stored in minor currency units (e.g., 2500 for 25.00 in major units)
        const formatPrice = (priceMinor: number | undefined) => {
          if (priceMinor == null) return "";
          // Convert from minor units (API) to major units (display)
          const priceMajor = minorToMajor(priceMinor);
          return ` (${formatMoney(priceMajor, true, currency)})`;
        };

        const getLabel = () => {
          switch (effectiveType) {
            case "per_unit":
              return <>{t("Per Session")}{formatPrice(pricePerUnit)}</>;
            case "per_month":
              return <>{t("Per Month")}{formatPrice(pricePerMonth)}</>;
            case "free":
              return <>{t("Free")}</>;
            default:
              return <span className="text-muted-foreground">-</span>;
          }
        };

        return (
          <span className={isOverridden ? "font-medium text-warning-on-surface" : ""}>
            {getLabel()}
            {isOverridden && <span className="ml-1" title={t("Billing overridden from enrollment default")}>*</span>}
          </span>
        );
      },
    },
  ];
}
